/**
 * One new outbound conversation start per ISO week (Masco 2026-08-22-K).
 * Existing matches stay chat-open forever once started; only the *first*
 * message to a person who has never been messaged by this user counts.
 */

import { writeLocal } from "../storage/localWrite";

const KEY = "un8.matching.newMessageWeek.v1";

type WeekRecord = {
  weekKey: string;
  /** Other user ids already counted this ISO week. */
  startedIds: string[];
  /** Lifetime: anyone you have already opened a new chat with. */
  everStartedIds: string[];
};

type MapFile = Record<string, WeekRecord>;

function weekKey(d: Date = new Date()): string {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function readAll(): MapFile {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    return JSON.parse(raw) as MapFile;
  } catch {
    return {};
  }
}

function writeAll(map: MapFile) {
  writeLocal(KEY, JSON.stringify(map));
}

function ensure(userId: string, now = new Date()): WeekRecord {
  const map = readAll();
  const key = weekKey(now);
  let rec = map[userId];
  if (!rec) {
    rec = { weekKey: key, startedIds: [], everStartedIds: [] };
    map[userId] = rec;
    writeAll(map);
    return rec;
  }
  if (rec.weekKey !== key) {
    rec = {
      weekKey: key,
      startedIds: [],
      everStartedIds: rec.everStartedIds ?? [],
    };
    map[userId] = rec;
    writeAll(map);
  }
  return rec;
}

export const NEW_MESSAGES_PER_WEEK = 1;

export const newMessageWeekStore = {
  weekKey,

  /** True if sending the first-ever message to otherId is allowed this week. */
  canStartNew(userId: string, otherId: string, now = new Date()): boolean {
    const rec = ensure(userId, now);
    if (rec.everStartedIds.includes(otherId)) return true; // continuing existing thread
    return rec.startedIds.length < NEW_MESSAGES_PER_WEEK;
  },

  remaining(userId: string, now = new Date()): number {
    const rec = ensure(userId, now);
    return Math.max(0, NEW_MESSAGES_PER_WEEK - rec.startedIds.length);
  },

  /**
   * Record an outbound first message. No-op if this other was messaged before.
   * Returns false when the weekly cap blocks a brand-new start.
   */
  recordStart(
    userId: string,
    otherId: string,
    now = new Date()
  ): { ok: true } | { ok: false; error: string } {
    const map = readAll();
    const rec = ensure(userId, now);
    if (rec.everStartedIds.includes(otherId)) return { ok: true };
    if (rec.startedIds.length >= NEW_MESSAGES_PER_WEEK) {
      return {
        ok: false,
        error: "You can message one new person per week. Existing chats stay open.",
      };
    }
    rec.startedIds = [...rec.startedIds, otherId];
    rec.everStartedIds = [...rec.everStartedIds, otherId];
    map[userId] = rec;
    writeAll(map);
    return { ok: true };
  },
};
